import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, ApiError, type MidiaComunicado, type TipoMidia } from "../api";
import { Avatar } from "./EndomarketingPecas";
import {
  type Colaborador,
  type EstiloArte,
  ESTILOS_ARTE,
  MAX_MIDIAS,
  MAX_MIDIA_MB,
  MODELOS_MENSAGEM,
  abrirJanela,
  aplicarVariaveis,
  baixarArquivo,
  baixarDataUrl,
  comExtensaoCerta,
  compactarImagem,
  compartilharArte,
  copiar,
  copiarImagem,
  dataUrlParaArquivo,
  gerarArteComunicado,
  guardarUsarWhatsAppWeb,
  lerUsarWhatsAppWeb,
  linkWhatsApp,
  norm,
  normalizarNumero,
  podeCompartilharArquivo,
  primeiroNome,
  tamanhoLegivel,
  temTelefone,
  textoParaGrupo,
  tipoDaMidia,
} from "../endomarketing-utils";
import {
  IconSearch,
  IconCopy,
  IconCheck,
  IconClose,
  IconTrash,
  IconChat,
  IconSparkles,
  IconUpload,
  IconDownload,
  IconFile,
  IconUsers,
} from "../icons";
import { usePodeExcluir } from "../permissoes";
import { ordenarPorNome, ordenarTextos } from "../ordem";

/**
 * COMUNICADO NO WHATSAPP — o disparo em massa do RH.
 *
 * O envio é pelo WhatsApp do próprio RH (wa.me): o sistema abre a conversa de
 * cada pessoa com o texto pronto e quem confirma é a pessoa que está operando.
 * Não usa API paga e nada sai sozinho.
 *
 * O fluxo da tela é o do trabalho real, em três passos:
 *   1. ESCREVER   — modelo pronto ou texto próprio, com {nome}/{setor};
 *      (e, se quiser, a MÍDIA do aviso: fotos, vídeos, PDF ou a arte gerada);
 *   2. ESCOLHER   — todos, um setor, ou marcados a dedo;
 *   3. DISPARAR   — o comunicado é registrado e vira uma FILA: abre uma conversa
 *      por vez (o navegador bloqueia dezenas de abas de uma vez) e a tela mostra
 *      quanto já foi. Ou, de uma vez, para um GRUPO do WhatsApp.
 */

/** Público gravado no histórico quando o envio é para um grupo. */
const PUBLICO_GRUPO = "Grupo do WhatsApp";

interface Historico {
  id: number;
  titulo: string;
  mensagem: string;
  publico: string | null;
  destinatarios: { id: number; nome: string; telefone: string | null }[];
  enviados: number;
  /** Primeira imagem que foi junto (compatibilidade com o histórico antigo). */
  arte: string | null;
  /** Tudo o que foi junto — o servidor já devolve a lista pronta (ver normalizarMidias). */
  midias: MidiaComunicado[];
  criado_por: string | null;
  criado_em: string;
}

/** Uma mídia na tela, antes e depois de subir. */
interface MidiaTela {
  /** Chave local da lista. */
  chave: string;
  /** Nome do arquivo no servidor (é o que o comunicado guarda). */
  arquivo: string;
  /** Nome original — o que aparece na tela e no arquivo baixado. */
  nome: string;
  tipo: TipoMidia;
  tamanho: number;
  /** De onde a tela lê a prévia: dataURL/endereço local, ou o do servidor. */
  src: string;
  /**
   * O arquivo em mãos — é o que o compartilhamento do sistema manda. Null =
   * ainda buscando no servidor (mídia que voltou do histórico).
   */
  file: File | null;
  /** A arte desenhada pelo sistema ("Gerar de novo" troca esta, não empilha). */
  gerada?: boolean;
  /** Não abriu (sumiu do servidor): precisa sair da lista antes do envio. */
  falhou?: boolean;
}

/** Como as mídias chegam ao WhatsApp NESTE aparelho (decide botões e dicas). */
type Caminho = "texto" | "compartilhar" | "copiar" | "baixar";

/** O navegador tem o compartilhamento do sistema com arquivo? (checagem fina é com os arquivos reais) */
const APARELHO_COMPARTILHA =
  typeof navigator !== "undefined" && typeof navigator.canShare === "function";

function novaChave(): string {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

export default function Comunicado({ onAviso }: { onAviso: (m: string) => void }) {
  const podeExcluir = usePodeExcluir(); // só admin apaga registro (regra do Portal)
  const [colaboradores, setColaboradores] = useState<Colaborador[]>([]);
  const [historico, setHistorico] = useState<Historico[]>([]);
  const [titulo, setTitulo] = useState("");
  const [mensagem, setMensagem] = useState("");
  const [setor, setSetor] = useState("");
  const [busca, setBusca] = useState("");
  const [marcados, setMarcados] = useState<Set<number>>(new Set());
  const [erro, setErro] = useState<string | null>(null);
  const [registrando, setRegistrando] = useState(false);
  const [enviandoGrupo, setEnviandoGrupo] = useState(false);
  /** Fila do disparo: quem falta abrir. Vazia = ainda não disparou. */
  const [fila, setFila] = useState<Colaborador[]>([]);
  const [enviados, setEnviados] = useState<Set<number>>(new Set());
  const [detalhe, setDetalhe] = useState<Historico | null>(null);

  /* ---- MÍDIA do aviso: fotos, vídeos, PDF e a arte gerada ---- */
  const [midias, setMidias] = useState<MidiaTela[]>([]);
  const [estiloArte, setEstiloArte] = useState<EstiloArte>("marca");
  /** O que está em andamento no bloco de mídia ("Enviando 1 de 3…"); null = livre. */
  const [ocupado, setOcupado] = useState<string | null>(null);
  const [erroMidia, setErroMidia] = useState<string | null>(null);
  /** As mídias desta lista já foram baixadas (muda a dica do envio no computador). */
  const [baixou, setBaixou] = useState(false);
  /** Este aparelho manda imagem pelo compartilhamento do sistema? (teste, para a dica) */
  const [mandaImagem, setMandaImagem] = useState(false);
  /** Ignorar a bandeja do sistema e ir pelo WhatsApp Web (preferência do aparelho). */
  const [usarWeb, setUsarWeb] = useState(lerUsarWhatsAppWeb);
  const midiaRef = useRef<HTMLInputElement>(null);
  /** Endereços locais (vídeo/PDF anexado) — soltos ao tirar da lista e ao sair da tela. */
  const urlsLocais = useRef(new Set<string>());

  const carregar = useCallback(async () => {
    try {
      const [f, h] = await Promise.all([
        api.endomarketing.fotos(),
        api.endomarketing.comunicados(),
      ]);
      // Grade de destinatários (e a fila que sai dela) em ordem alfabética.
      setColaboradores(ordenarPorNome(f.colaboradores ?? [], (c) => c.nome));
      setHistorico(h ?? []);
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : "Erro ao carregar.");
    }
  }, []);

  useEffect(() => {
    carregar();
  }, [carregar]);

  /**
   * Pergunta ao APARELHO se ele consegue mandar imagem pelo compartilhamento
   * do sistema (o caminho em que o WhatsApp recebe a mídia com a mensagem como
   * legenda). É uma checagem real, com um arquivo de mentira: o suporte muda
   * por navegador e exige HTTPS, e prometer o que não funciona é pior do que
   * já oferecer o caminho de copiar/baixar. Com as mídias de verdade na lista,
   * quem decide é a checagem com os arquivos reais (ver `caminho`).
   */
  useEffect(() => {
    try {
      const teste = new File([new Blob(["."], { type: "image/png" })], "teste.png", {
        type: "image/png",
      });
      setMandaImagem(podeCompartilharArquivo(teste));
    } catch {
      setMandaImagem(false);
    }
  }, []);

  // Ao sair da tela, solta os endereços locais dos vídeos/PDFs anexados.
  useEffect(() => {
    const urls = urlsLocais.current;
    return () => {
      for (const u of urls) URL.revokeObjectURL(u);
      urls.clear();
    };
  }, []);

  function soltarUrl(src: string) {
    if (urlsLocais.current.has(src)) {
      URL.revokeObjectURL(src);
      urlsLocais.current.delete(src);
    }
  }

  function limparMidias() {
    for (const m of midias) soltarUrl(m.src);
    setMidias([]);
    setBaixou(false);
    setErroMidia(null);
  }

  /** Desenha a arte com o título e o texto do comunicado (canvas, no aparelho). */
  async function gerarArte() {
    setErroMidia(null);
    if (!titulo.trim() && !mensagem.trim()) {
      return setErroMidia("Escreva o título e a mensagem antes de gerar a arte.");
    }
    const anterior = midias.find((m) => m.gerada);
    if (!anterior && midias.length >= MAX_MIDIAS) {
      return setErroMidia(`O aviso já tem ${MAX_MIDIAS} mídias — tire uma para gerar a arte.`);
    }
    setOcupado("Gerando a arte…");
    try {
      const dataUrl = await gerarArteComunicado({
        titulo: titulo.trim() || "Comunicado",
        texto: mensagem.trim(),
        estilo: estiloArte,
      });
      // Sobe na hora: assim o histórico guarda a arte junto do comunicado e ela
      // pode ser reaproveitada depois, sem depender de a pessoa salvar nada.
      const file = await dataUrlParaArquivo(dataUrl, "arte-comunicado.png");
      const r = await api.endomarketing.arte.enviar(file);
      const item: MidiaTela = {
        chave: novaChave(),
        arquivo: r.arquivo,
        nome: "arte-comunicado.png",
        tipo: "imagem",
        tamanho: file.size,
        src: dataUrl,
        file,
        gerada: true,
      };
      // "Gerar de novo" troca a arte anterior no mesmo lugar, em vez de
      // empilhar uma segunda arte quase igual.
      setMidias((lista) =>
        anterior && lista.some((m) => m.chave === anterior.chave)
          ? lista.map((m) => (m.chave === anterior.chave ? item : m))
          : [...lista, item],
      );
      setBaixou(false);
    } catch (e) {
      setErroMidia(e instanceof Error ? e.message : "Não foi possível gerar a arte.");
    } finally {
      setOcupado(null);
    }
  }

  /**
   * Prepara e sobe UM arquivo escolhido. Foto é compactada para 1080px (arte de
   * WhatsApp não precisa de mais, e foto de celular moderna passa de 5 MB);
   * vídeo e PDF sobem como estão. GIF também — no canvas perderia a animação.
   */
  async function prepararESubir(original: File): Promise<MidiaTela> {
    const tipo = tipoDaMidia(original);
    if (!tipo) throw new Error("não é foto, vídeo nem PDF.");

    if (tipo === "imagem" && !/gif$/i.test(original.type)) {
      const dataUrl = await compactarImagem(original, 1080, 0.9);
      const nome = `${original.name.replace(/\.[^.]*$/, "").slice(0, 80) || "foto"}.jpg`;
      const file = await dataUrlParaArquivo(dataUrl, nome);
      const r = await api.endomarketing.arte.enviar(file);
      return { chave: novaChave(), arquivo: r.arquivo, nome, tipo, tamanho: file.size, src: dataUrl, file };
    }

    const file = comExtensaoCerta(original);
    const r = await api.endomarketing.arte.enviar(file);
    const src = URL.createObjectURL(file);
    urlsLocais.current.add(src);
    return { chave: novaChave(), arquivo: r.arquivo, nome: file.name, tipo: r.tipo ?? tipo, tamanho: file.size, src, file };
  }

  /** Fotos, vídeos e PDFs escolhidos no botão "Anexar". */
  async function anexarMidias(lista: FileList | null) {
    const escolhidos = Array.from(lista ?? []);
    if (midiaRef.current) midiaRef.current.value = "";
    if (escolhidos.length === 0) return;
    setErroMidia(null);

    const vagas = MAX_MIDIAS - midias.length;
    if (vagas <= 0) {
      return setErroMidia(`Cada aviso leva até ${MAX_MIDIAS} mídias. Tire alguma para anexar outra.`);
    }
    // Confere TUDO antes de subir qualquer um: recusar no meio deixaria metade
    // anexada e a pessoa sem saber o que entrou.
    for (const f of escolhidos) {
      const tipo = tipoDaMidia(f);
      if (!tipo) return setErroMidia(`"${f.name}" não é foto, vídeo nem PDF.`);
      if (tipo !== "imagem" && f.size > MAX_MIDIA_MB * 1024 * 1024) {
        return setErroMidia(
          `"${f.name}" tem ${tamanhoLegivel(f.size)}. O máximo para ${
            tipo === "video" ? "vídeo" : "PDF"
          } é ${MAX_MIDIA_MB} MB — encurte o vídeo ou mande o arquivo em partes.`,
        );
      }
    }

    const aSubir = escolhidos.slice(0, vagas);
    const deFora = escolhidos.length - aSubir.length;
    let falha: string | null = null;
    for (const [i, original] of aSubir.entries()) {
      setOcupado(`Enviando ${i + 1} de ${aSubir.length}: ${original.name}…`);
      try {
        const item = await prepararESubir(original);
        setMidias((l) => [...l, item]);
      } catch (e) {
        falha = `"${original.name}": ${e instanceof Error ? e.message : "não foi possível enviar."}`;
        break;
      }
    }
    setOcupado(null);
    setBaixou(false);
    const aviso = [
      falha,
      deFora > 0 ? `Só cabem ${MAX_MIDIAS} mídias por aviso — ${deFora} arquivo(s) ficaram de fora.` : null,
    ].filter(Boolean);
    if (aviso.length) setErroMidia(aviso.join(" "));
  }

  function removerMidia(chave: string) {
    const m = midias.find((x) => x.chave === chave);
    if (m) soltarUrl(m.src);
    setMidias((l) => l.filter((x) => x.chave !== chave));
    setBaixou(false);
  }

  /**
   * Mídia que voltou do histórico existe só no servidor. Para o compartilhamento
   * do sistema ela precisa estar em mãos como ARQUIVO antes do clique (a bandeja
   * só abre com o clique "fresco"), então é buscada já, em segundo plano.
   */
  function buscarArquivos(itens: MidiaTela[]) {
    for (const m of itens) {
      fetch(m.src)
        .then((r) => {
          if (!r.ok) throw new Error(String(r.status));
          return r.blob();
        })
        .then((blob) => {
          const file = new File([blob], m.nome, { type: blob.type });
          setMidias((l) =>
            l.map((x) => (x.chave === m.chave ? { ...x, file, tamanho: x.tamanho || file.size } : x)),
          );
        })
        .catch(() => setMidias((l) => l.map((x) => (x.chave === m.chave ? { ...x, falhou: true } : x))));
    }
  }

  /** Baixa todas as mídias de uma vez (o caminho do computador para vídeo/PDF). */
  async function baixarMidias() {
    for (const [i, m] of midias.entries()) {
      if (m.file) baixarArquivo(m.file, m.nome);
      else baixarDataUrl(m.src, m.nome);
      // Downloads disparados no mesmo instante são descartados por alguns
      // navegadores: um respiro curto entre um e outro.
      if (i < midias.length - 1) await new Promise((r) => setTimeout(r, 400));
    }
    setBaixou(true);
    if (midias.length > 1) {
      onAviso("Se o navegador perguntar, permita baixar vários arquivos deste site.");
    }
  }

  const setores = useMemo(
    () => ordenarTextos([...new Set(colaboradores.map((c) => c.departamento).filter(Boolean))] as string[]),
    [colaboradores],
  );

  /** Quem está na tela agora (setor + busca). */
  const visiveis = useMemo(() => {
    const q = norm(busca);
    return colaboradores.filter(
      (c) =>
        (!setor || c.departamento === setor) &&
        (!q || norm(c.nome).includes(q) || norm(c.cargo).includes(q)),
    );
  }, [colaboradores, setor, busca]);

  const selecionados = useMemo(
    () => colaboradores.filter((c) => marcados.has(c.id)),
    [colaboradores, marcados],
  );
  const comTelefone = useMemo(() => selecionados.filter(temTelefone), [selecionados]);
  const semTelefone = selecionados.length - comTelefone.length;

  function alternar(id: number) {
    const novo = new Set(marcados);
    novo.has(id) ? novo.delete(id) : novo.add(id);
    setMarcados(novo);
  }

  function marcarTodosVisiveis() {
    const novo = new Set(marcados);
    for (const c of visiveis) if (temTelefone(c)) novo.add(c.id);
    setMarcados(novo);
  }

  function usarModelo(key: string) {
    const m = MODELOS_MENSAGEM.find((x) => x.key === key);
    if (!m) return;
    setTitulo(m.titulo);
    setMensagem(m.texto);
  }

  /** Texto final de quem está na frente da fila (a prévia mostra este). */
  const exemplo = comTelefone[0] ?? selecionados[0] ?? colaboradores[0];
  const previa = exemplo ? aplicarVariaveis(mensagem, exemplo) : mensagem;

  /* ---- como as mídias chegam ao WhatsApp NESTE aparelho ---- */
  /** Mídia do histórico ainda vindo do servidor (só importa onde há compartilhamento). */
  const aguardandoArquivos =
    APARELHO_COMPARTILHA && !usarWeb && midias.some((m) => !m.file && !m.falhou);
  const comFalha = midias.some((m) => m.falhou);
  const umaImagemSo = midias.length === 1 && midias[0]!.tipo === "imagem";
  const compartilhaTudo = useMemo(
    () =>
      !usarWeb &&
      midias.length > 0 &&
      midias.every((m) => m.file) &&
      podeCompartilharArquivo(midias.map((m) => m.file as File)),
    [midias, usarWeb],
  );
  const caminho: Caminho =
    midias.length === 0 ? "texto" : compartilhaTudo ? "compartilhar" : umaImagemSo ? "copiar" : "baixar";
  const primeiraImagem = midias.find((m) => m.tipo === "imagem");

  /** O que vai para o registro: só os nomes (o servidor confere o resto). */
  const midiasParaRegistro = () => midias.map((m) => ({ arquivo: m.arquivo, nome: m.nome }));

  /** O que impede de registrar/enviar — igual para a fila e para o grupo. */
  function conferirAntes(): string | null {
    if (!titulo.trim()) return "Dê um título ao comunicado (é o que fica no histórico).";
    if (!mensagem.trim()) return "Escreva a mensagem.";
    if (ocupado) return "Espere as mídias terminarem de subir.";
    if (comFalha) return "Uma das mídias não está mais no servidor — tire-a da lista (ou anexe de novo).";
    return null;
  }

  async function dispararAgora() {
    setErro(null);
    const problema = conferirAntes();
    if (problema) return setErro(problema);
    if (comTelefone.length === 0) {
      return setErro("Marque ao menos uma pessoa com telefone no cadastro — ou use “Enviar para um grupo”.");
    }
    setRegistrando(true);
    try {
      await api.endomarketing.registrarComunicado({
        titulo: titulo.trim(),
        mensagem: mensagem.trim(),
        publico: setor || (marcados.size >= colaboradores.length ? "Todos" : "Selecionados"),
        destinatarios: comTelefone.map((c) => ({ id: c.id, nome: c.nome, telefone: c.telefone })),
        midias: midiasParaRegistro(),
        arte: primeiraImagem?.arquivo ?? null,
      });
      setFila(comTelefone);
      setEnviados(new Set());
      await carregar();
      onAviso(
        midias.length === 0
          ? `Comunicado registrado para ${comTelefone.length} pessoa(s). Agora abra as conversas — uma de cada vez.`
          : `Comunicado com ${midias.length} mídia(s) registrado para ${comTelefone.length} pessoa(s). ${
              caminho === "compartilhar"
                ? "Agora envie um por vez — as mídias vão junto, com a mensagem de legenda."
                : caminho === "copiar"
                  ? "Agora abra as conversas: a imagem é copiada e você cola com Ctrl+V na conversa."
                  : "Baixe as mídias uma vez e, em cada conversa que abrir, arraste os arquivos."
            }`,
      );
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : "Não foi possível registrar o comunicado.");
    } finally {
      setRegistrando(false);
    }
  }

  /**
   * Leva a mensagem (e as mídias) ao WhatsApp. O caminho muda conforme o que
   * ESTE aparelho consegue:
   *
   *  • compartilha todos os arquivos → bandeja do sistema: o WhatsApp abre com
   *    as mídias e a mensagem de legenda (quem escolhe a conversa é a pessoa,
   *    porque esse caminho não aceita número pré-definido);
   *  • computador com UMA imagem → copia a imagem e abre a conversa pelo
   *    número; é só colar (Ctrl+V) e enviar — o texto já está na caixa;
   *  • computador com vídeo/PDF ou várias mídias → abre a conversa com o texto;
   *    os arquivos baixados (botão "Baixar as mídias") são arrastados para ela;
   *  • sem mídia → como sempre foi: abre a conversa com o texto pronto.
   *
   * `numero` vazio = grupo: o WhatsApp abre para escolher a conversa.
   * Devolve se saiu e o recado para a tela (quem chama decide como mostrar).
   */
  async function levarAoWhatsApp(
    numero: string,
    texto: string,
    onde: string,
  ): Promise<{ foi: boolean; recado: string | null }> {
    if (caminho === "compartilhar") {
      try {
        const foi = await compartilharArte(midias.map((m) => m.file as File), texto);
        // false = fechou a bandeja: não conta como enviado. No computador, isso
        // costuma ser o WhatsApp que não aparece na lista do Windows.
        return {
          foi,
          recado: foi
            ? null
            : "Envio cancelado. Se o WhatsApp não apareceu na lista, marque “Usar o WhatsApp Web neste aparelho” (no bloco da mídia) e envie de novo.",
        };
      } catch {
        // Compartilhamento falhou: segue pelo caminho do wa.me em vez de travar.
      }
    }

    let recado: string | null = null;
    if (umaImagemSo) {
      // Copia ANTES de abrir a aba: com a aba nova na frente, a página perde o
      // foco e o navegador recusa escrever na área de transferência.
      const copiou = await copiarImagem(midias[0]!.src);
      recado = copiou
        ? `Imagem copiada. ${onde}, cole com Ctrl+V e envie — o texto já está na caixa.`
        : `Não consegui copiar a imagem neste navegador. Use “Baixar as mídias” e anexe ${onde.toLowerCase()}.`;
    } else if (midias.length > 0) {
      recado = baixou
        ? `${onde}, arraste os arquivos baixados para a janela do WhatsApp (ou anexe pelo clipe) e envie — o texto já está na caixa.`
        : `Antes, baixe as mídias (botão “Baixar as mídias”) e arraste os arquivos ${onde.toLowerCase()}.`;
    }

    // O navegador bloqueia abrir várias abas de uma vez; por isso é um clique
    // por pessoa, e a tela guarda quem já foi.
    if (!abrirJanela(linkWhatsApp(numero, texto))) {
      return {
        foi: false,
        recado: "O navegador bloqueou a janela. Libere os pop-ups deste site para abrir as conversas.",
      };
    }
    return { foi: true, recado };
  }

  /** Manda para UMA pessoa da fila. */
  async function abrirConversa(c: Colaborador) {
    if (aguardandoArquivos) return;
    const { foi, recado } = await levarAoWhatsApp(
      normalizarNumero(c.telefone),
      aplicarVariaveis(mensagem, c),
      `Na conversa de ${primeiroNome(c.nome)}`,
    );
    if (recado) onAviso(recado);
    if (foi) setEnviados((prev) => new Set(prev).add(c.id));
  }

  /**
   * ENVIAR PARA UM GRUPO: não precisa marcar ninguém. Abre o WhatsApp para a
   * pessoa escolher o grupo e registra no histórico como "Grupo do WhatsApp".
   *
   * Abre ANTES de registrar: a bandeja do celular (e a aba nova) só abrem
   * enquanto o clique está "fresco" — esperar o servidor antes faz o navegador
   * recusar. E assim o histórico fica com o que de fato saiu.
   */
  async function enviarParaGrupo() {
    setErro(null);
    const problema = conferirAntes();
    if (problema) return setErro(problema);
    if (aguardandoArquivos) return;
    setEnviandoGrupo(true);
    try {
      const { foi, recado } = await levarAoWhatsApp(
        "",
        textoParaGrupo(mensagem.trim(), setor),
        "No grupo que você escolher",
      );
      if (!foi) {
        if (recado) onAviso(recado);
        return;
      }
      try {
        await api.endomarketing.registrarComunicado({
          titulo: titulo.trim(),
          mensagem: mensagem.trim(),
          publico: PUBLICO_GRUPO,
          destinatarios: [],
          midias: midiasParaRegistro(),
          arte: primeiraImagem?.arquivo ?? null,
        });
        await carregar();
        onAviso(recado ? `${recado} (Registrado no histórico.)` : "Enviado para o grupo e registrado no histórico.");
      } catch (e) {
        if (recado) onAviso(recado);
        setErro(
          `O WhatsApp abriu, mas o envio não entrou no histórico: ${
            e instanceof ApiError ? e.message : "erro ao registrar."
          }`,
        );
      }
    } finally {
      setEnviandoGrupo(false);
    }
  }

  function reutilizar(h: Historico) {
    setTitulo(h.titulo);
    setMensagem(h.mensagem);
    setFila([]);
    setEnviados(new Set());
    limparMidias();
    // As mídias voltam junto: é o mesmo material da campanha, e refazê-lo à
    // mão é o que faria a pessoa desistir.
    const itens: MidiaTela[] = (h.midias ?? []).map((m) => ({
      chave: novaChave(),
      arquivo: m.arquivo,
      nome: m.nome || m.arquivo,
      tipo: m.tipo,
      tamanho: m.tamanho ?? 0,
      src: api.endomarketing.arte.url(m.arquivo),
      file: null,
    }));
    setMidias(itens);
    if (itens.length && APARELHO_COMPARTILHA) buscarArquivos(itens);
    // (Busca mesmo com o WhatsApp Web ligado: desligar a opção depois não pode
    // deixar a lista sem os arquivos em mãos.)
    onAviso(
      itens.length
        ? `Texto e ${itens.length} mídia(s) carregados — escolha quem recebe.`
        : "Texto carregado no formulário — escolha quem recebe.",
    );
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  const proximo = fila.find((c) => !enviados.has(c.id));

  function rotuloProximo(c: Colaborador): string {
    const n = primeiroNome(c.nome);
    if (aguardandoArquivos) return "Preparando as mídias…";
    if (caminho === "compartilhar") {
      return `Enviar com ${midias.length > 1 ? "as mídias" : umaImagemSo ? "a imagem" : "a mídia"} — ${n}`;
    }
    if (caminho === "copiar") return `Copiar a imagem e abrir — ${n}`;
    return `Abrir a próxima conversa — ${n}`;
  }

  return (
    <div className="endo-comunicado">
      {/* ---------- passo 1: a mensagem ---------- */}
      <section className="endo-bloco">
        <header className="endo-bloco-head">
          <h3>
            <IconChat size={16} /> 1. A mensagem
          </h3>
          <span className="muted">{mensagem.length} caractere(s)</span>
        </header>

        <div className="endo-modelos">
          {MODELOS_MENSAGEM.map((m) => (
            <button
              key={m.key}
              type="button"
              className="endo-chip"
              onClick={() => usarModelo(m.key)}
              title="Preencher com este modelo (dá para editar depois)"
            >
              {m.nome}
            </button>
          ))}
        </div>

        <div className="endo-msg-grade">
          <div className="endo-editor">
            <label className="field">
              <span>
                Título <em className="req">*</em>{" "}
                <small className="muted">(só para o histórico — não vai na mensagem)</small>
              </span>
              <input
                value={titulo}
                onChange={(e) => setTitulo(e.target.value)}
                placeholder="Ex.: Campanha de segurança — agosto"
              />
            </label>
            <label className="field">
              <span>
                Mensagem <em className="req">*</em>{" "}
                <small className="muted">
                  <code>{"{nome}"}</code> <code>{"{setor}"}</code> <code>{"{cargo}"}</code>
                </small>
              </span>
              <textarea
                rows={9}
                value={mensagem}
                onChange={(e) => setMensagem(e.target.value)}
                placeholder="Olá, {nome}! Lembrete: a campanha de uso de EPI começa segunda…"
              />
            </label>
          </div>

          {/* Prévia: como a pessoa vai receber, já com o nome trocado. As
              mídias aparecem ACIMA do balão — é assim que chegam no WhatsApp. */}
          <div className="endo-previa">
            <span className="endo-previa-titulo">
              Como {exemplo ? primeiroNome(exemplo.nome) : "a pessoa"} vai receber
            </span>
            {midias.length > 0 && (
              <div className={`endo-previa-midias ${midias.length === 1 ? "unica" : ""}`}>
                {midias.slice(0, 6).map((m) => (
                  <MiniaturaMidia key={m.chave} m={m} />
                ))}
              </div>
            )}
            <div className="endo-balao">{previa || "A mensagem aparece aqui."}</div>
            <button
              type="button"
              className="btn-secondary btn-sm"
              disabled={!mensagem.trim()}
              onClick={async () =>
                onAviso((await copiar(previa)) ? "Mensagem copiada." : "Não foi possível copiar.")
              }
            >
              <IconCopy size={15} /> Copiar mensagem
            </button>
          </div>
        </div>
      </section>

      {/* ---------- mídia do aviso (opcional) ---------- */}
      <section className="endo-bloco">
        <header className="endo-bloco-head">
          <h3>
            <IconSparkles size={16} /> Mídia do aviso <small className="muted">(opcional)</small>
          </h3>
          {midias.length > 0 && (
            <span className="badge">
              {midias.length} de {MAX_MIDIAS}
            </span>
          )}
        </header>

        <div className="endo-midia">
          <div className="endo-midia-acoes">
            <label className="field endo-midia-estilo">
              <span>Estilo da arte gerada</span>
              <select value={estiloArte} onChange={(e) => setEstiloArte(e.target.value as EstiloArte)}>
                {ESTILOS_ARTE.map((e) => (
                  <option key={e.key} value={e.key}>
                    {e.nome}
                  </option>
                ))}
              </select>
            </label>

            <div className="endo-arte-botoes">
              <button
                type="button"
                className="btn-ia btn-sm"
                onClick={gerarArte}
                disabled={!!ocupado}
                data-grava
                title="Desenha a arte com o título e o texto que você escreveu"
              >
                <IconSparkles size={15} />{" "}
                {midias.some((m) => m.gerada) ? "Gerar a arte de novo" : "Gerar arte com o texto"}
              </button>
              <button
                type="button"
                className="btn-secondary btn-sm"
                onClick={() => midiaRef.current?.click()}
                disabled={!!ocupado || midias.length >= MAX_MIDIAS}
                title={`Fotos, vídeos (até ${MAX_MIDIA_MB} MB) ou PDF — até ${MAX_MIDIAS} por aviso`}
              >
                <IconUpload size={15} /> Anexar fotos, vídeos ou PDF
              </button>
              <input
                ref={midiaRef}
                type="file"
                hidden
                multiple
                accept="image/*,video/*,application/pdf"
                onChange={(e) => anexarMidias(e.target.files)}
              />
              {midias.length > 0 && (
                <button type="button" className="btn-secondary btn-sm" onClick={baixarMidias}>
                  <IconDownload size={15} /> Baixar as mídias
                </button>
              )}
            </div>
          </div>

          {ocupado && <p className="endo-post-ocupado">{ocupado}</p>}
          {erroMidia && <div className="form-error">{erroMidia}</div>}

          {midias.length > 0 ? (
            <div className="endo-midias">
              {midias.map((m) => (
                <figure key={m.chave} className={`endo-midia-item ${m.falhou ? "falhou" : ""}`}>
                  <MiniaturaMidia m={m} />
                  <figcaption>
                    <span title={m.nome}>{m.nome}</span>
                    <small>{m.falhou ? "não abriu" : m.tamanho ? tamanhoLegivel(m.tamanho) : ""}</small>
                    <button
                      type="button"
                      className="icon-btn icon-btn-danger"
                      title="Remover da mensagem"
                      aria-label={`Remover ${m.nome} da mensagem`}
                      onClick={() => removerMidia(m.chave)}
                    >
                      <IconClose size={14} />
                    </button>
                  </figcaption>
                </figure>
              ))}
            </div>
          ) : (
            <div className="endo-arte-vazia endo-midia-vazia">
              Sem mídia — vai só o texto.
              <small>
                Gere a arte com o texto ou anexe fotos, vídeos (até {MAX_MIDIA_MB} MB) e PDF — até{" "}
                {MAX_MIDIAS} por aviso.
              </small>
            </div>
          )}

          {/* A tela é honesta sobre o que ESTE aparelho consegue fazer: o link
              do WhatsApp não carrega arquivo, e o caminho muda no celular e no
              computador (e, no celular, conforme o tipo de arquivo). */}
          <p className="endo-arte-dica">
            {midias.length === 0 ? (
              mandaImagem && !usarWeb ? (
                <>
                  <IconCheck size={14} /> Neste aparelho, o que você anexar vai <strong>junto</strong>{" "}
                  da mensagem, pelo compartilhamento do sistema (quando o aparelho aceita o tipo de
                  arquivo).
                </>
              ) : (
                <>
                  Neste computador o link do WhatsApp só leva texto: a <strong>foto</strong> é copiada
                  para colar com <strong>Ctrl+V</strong>; <strong>vídeo e PDF</strong> são baixados para
                  arrastar na conversa. Pelo celular, em geral vão junto.
                </>
              )
            ) : aguardandoArquivos ? (
              <>Preparando as mídias deste aviso para o envio…</>
            ) : caminho === "compartilhar" ? (
              <>
                <IconCheck size={14} /> Neste aparelho {midias.length > 1 ? "as mídias vão" : "a mídia vai"}{" "}
                <strong>junto</strong> da mensagem: ao enviar, o WhatsApp abre com{" "}
                {midias.length > 1 ? "os arquivos" : "o arquivo"} e o texto de legenda — você escolhe a
                pessoa (ou o grupo) na hora.
              </>
            ) : caminho === "copiar" ? (
              <>
                Neste computador o WhatsApp não aceita a imagem pelo link. Ao enviar, a imagem é{" "}
                <strong>copiada</strong> e a conversa abre com o texto pronto: é só colar com{" "}
                <strong>Ctrl+V</strong> e mandar. Pelo <strong>celular</strong>, ela vai junto
                automaticamente.
              </>
            ) : (
              <>
                {mandaImagem && !usarWeb
                  ? "Este aparelho não consegue mandar estes arquivos juntos pelo compartilhamento"
                  : "Pelo link, o WhatsApp só recebe o texto"}
                : <strong>baixe as mídias</strong> uma vez e, em cada conversa que abrir,{" "}
                <strong>arraste os arquivos</strong> baixados para a janela do WhatsApp (ou anexe
                pelo clipe).
              </>
            )}
          </p>
          {/* O Chrome do Windows também "compartilha" — pela bandeja do Windows,
              que só tem o WhatsApp se o aplicativo estiver instalado. */}
          {mandaImagem && (
            <label className="endo-web">
              <input
                type="checkbox"
                checked={usarWeb}
                onChange={(e) => {
                  setUsarWeb(e.target.checked);
                  guardarUsarWhatsAppWeb(e.target.checked);
                }}
              />
              <span>
                Usar o WhatsApp Web neste aparelho{" "}
                <small className="muted">(quando o WhatsApp não aparece na bandeja do sistema)</small>
              </span>
            </label>
          )}
        </div>
      </section>

      {/* ---------- passo 2: quem recebe ---------- */}
      <section className="endo-bloco">
        <header className="endo-bloco-head">
          <h3>2. Quem recebe</h3>
          <span className="badge">
            {selecionados.length} marcado(s) · {comTelefone.length} com WhatsApp
          </span>
        </header>

        <div className="endo-filtros">
          <div className="search-wrap">
            <IconSearch size={16} />
            <input
              className="search"
              placeholder="Buscar pessoa..."
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
            />
          </div>
          <div className="endo-chips">
            <button
              type="button"
              className={`endo-chip ${setor === "" ? "ativo" : ""}`}
              onClick={() => setSetor("")}
            >
              Todos os setores
            </button>
            {setores.map((s) => (
              <button
                key={s}
                type="button"
                className={`endo-chip ${setor === s ? "ativo" : ""}`}
                onClick={() => setSetor(s)}
              >
                {s}
              </button>
            ))}
          </div>
          <div className="endo-sel-acoes">
            <button className="btn-secondary btn-sm" onClick={marcarTodosVisiveis}>
              Marcar os {visiveis.filter(temTelefone).length} da lista
            </button>
            <button className="btn-secondary btn-sm" onClick={() => setMarcados(new Set())}>
              Limpar
            </button>
          </div>
        </div>

        <div className="endo-lista-dest">
          {visiveis.length === 0 && <div className="empty">Ninguém com esses filtros.</div>}
          {visiveis.map((c) => {
            const tem = temTelefone(c);
            return (
              <label key={c.id} className={`endo-dest ${tem ? "" : "sem-telefone"} ${marcados.has(c.id) ? "marcado" : ""}`}>
                <input
                  type="checkbox"
                  checked={marcados.has(c.id)}
                  disabled={!tem}
                  onChange={() => alternar(c.id)}
                />
                <Avatar pessoa={c} tamanho="s" />
                <div className="endo-dest-info">
                  <strong>{c.nome}</strong>
                  <small className="muted">
                    {c.departamento || c.cargo || "—"} · {tem ? c.telefone : "sem telefone"}
                  </small>
                </div>
              </label>
            );
          })}
        </div>

        {semTelefone > 0 && (
          <p className="endo-alerta">
            {semTelefone} pessoa(s) marcada(s) sem telefone ficam de fora do disparo.
          </p>
        )}
      </section>

      {/* ---------- passo 3: disparo ---------- */}
      <section className="endo-bloco">
        <header className="endo-bloco-head">
          <h3>3. Disparar</h3>
          {fila.length > 0 && (
            <span className="badge">
              {enviados.size} de {fila.length} aberto(s)
            </span>
          )}
        </header>

        {erro && <div className="form-error">{erro}</div>}

        <div className="endo-disparo">
          <button className="btn-primary" onClick={dispararAgora} disabled={registrando || enviandoGrupo}>
            {registrando ? "Registrando…" : `Registrar e preparar (${comTelefone.length})`}
          </button>
          <button
            type="button"
            className="btn-secondary"
            onClick={enviarParaGrupo}
            disabled={registrando || enviandoGrupo || aguardandoArquivos || !!ocupado}
            title="Não precisa marcar ninguém: abre o WhatsApp para você escolher o grupo"
          >
            <IconUsers size={16} /> Enviar para um grupo
          </button>
          {proximo && (
            <button
              className="btn-whats"
              onClick={() => abrirConversa(proximo)}
              disabled={aguardandoArquivos}
            >
              {rotuloProximo(proximo)}
            </button>
          )}
          {fila.length > 0 && !proximo && (
            <span className="endo-ok">
              <IconCheck size={16} /> Todas as {fila.length} conversas foram abertas.
            </span>
          )}
        </div>

        <p className="endo-disparo-dica muted">
          “Enviar para um grupo” não precisa marcar ninguém: abre o WhatsApp para você escolher o
          grupo e registra no histórico. No grupo, <code>{"{nome}"}</code> vira “pessoal”.
        </p>

        {/* Computador com vídeo/PDF (ou várias mídias): o link não leva
            arquivo — baixar UMA vez e arrastar em cada conversa. */}
        {caminho === "baixar" && !aguardandoArquivos && (
          <div className="endo-baixar-midias">
            <span>
              {baixou ? (
                <>
                  <IconCheck size={14} /> Mídias baixadas. Em cada conversa que abrir,{" "}
                  <strong>arraste os arquivos</strong> para a janela do WhatsApp (ou anexe pelo clipe).
                </>
              ) : (
                <>
                  Baixe as mídias <strong>uma vez</strong> e, em cada conversa que abrir,{" "}
                  <strong>arraste os arquivos</strong> para a janela do WhatsApp (ou anexe pelo clipe).
                </>
              )}
            </span>
            <button type="button" className="btn-secondary btn-sm" onClick={baixarMidias}>
              <IconDownload size={15} /> {baixou ? "Baixar de novo" : "Baixar as mídias"}
            </button>
          </div>
        )}

        {fila.length > 0 && (
          <>
            <div className="endo-barra">
              <div
                className="endo-barra-fill"
                style={{ width: `${Math.round((enviados.size / fila.length) * 100)}%` }}
              />
            </div>
            <div className="endo-fila">
              {fila.map((c) => (
                <button
                  key={c.id}
                  className={`btn-whats btn-sm ${enviados.has(c.id) ? "ja-enviado" : ""}`}
                  onClick={() => abrirConversa(c)}
                  disabled={aguardandoArquivos}
                  title={`Abrir a conversa de ${c.nome}`}
                >
                  {enviados.has(c.id) && <IconCheck size={14} />} {primeiroNome(c.nome)}
                </button>
              ))}
            </div>
          </>
        )}
      </section>

      {/* ---------- histórico ---------- */}
      {historico.length > 0 && (
        <section className="endo-bloco">
          <header className="endo-bloco-head">
            <h3>Comunicados anteriores</h3>
            <span className="muted">{historico.length} registro(s)</span>
          </header>
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Título</th>
                  <th>Público</th>
                  <th>Pessoas</th>
                  <th>Quando</th>
                  <th className="col-actions">Ações</th>
                </tr>
              </thead>
              <tbody>
                {historico.map((h) => (
                  <tr key={h.id}>
                    <td>
                      <button className="link-btn" onClick={() => setDetalhe(h)}>
                        {h.titulo}
                      </button>
                      {(h.midias?.length ?? 0) > 0 && (
                        <small className="muted endo-hist-midias"> · {h.midias.length} mídia(s)</small>
                      )}
                    </td>
                    <td>{h.publico || "—"}</td>
                    <td>{h.publico === PUBLICO_GRUPO && !h.enviados ? "grupo" : h.enviados}</td>
                    <td>
                      {new Date(h.criado_em).toLocaleString("pt-BR", {
                        dateStyle: "short",
                        timeStyle: "short",
                      })}
                    </td>
                    <td className="col-actions">
                      <button
                        className="btn-secondary btn-sm"
                        title="Usar este texto (e as mídias) de novo"
                        onClick={() => reutilizar(h)}
                      >
                        Reutilizar
                      </button>
                      {podeExcluir && (
                        <button
                          className="icon-btn icon-btn-danger"
                          title="Excluir do histórico"
                          onClick={async () => {
                            if (!confirm(`Excluir "${h.titulo}" do histórico?`)) return;
                            await api.endomarketing.removerComunicado(h.id);
                            await carregar();
                          }}
                        >
                          <IconTrash size={16} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {detalhe && <DetalheComunicado h={detalhe} onClose={() => setDetalhe(null)} />}
    </div>
  );
}

/**
 * Miniatura de uma mídia: a foto, o primeiro quadro do vídeo ou o ícone do
 * PDF. O "#t=0.1" faz o Safari do iPhone desenhar um quadro do vídeo — sem ele
 * a miniatura fica preta até dar play.
 */
function MiniaturaMidia({ m }: { m: MidiaTela }) {
  if (m.tipo === "imagem") return <img src={m.src} alt={m.nome} loading="lazy" />;
  if (m.tipo === "video") {
    return (
      <div className="endo-midia-video">
        <video src={`${m.src}#t=0.1`} muted playsInline preload="metadata" aria-label={m.nome} />
        <span className="endo-midia-tipo">vídeo</span>
      </div>
    );
  }
  return (
    <div className="endo-midia-pdf" title={m.nome}>
      <IconFile size={26} />
      <span>PDF</span>
    </div>
  );
}

function DetalheComunicado({ h, onClose }: { h: Historico; onClose: () => void }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // "Quem recebeu" em ordem alfabética (cópia — o registro não é tocado).
  const lista = ordenarPorNome(Array.isArray(h.destinatarios) ? h.destinatarios : [], (d) => d.nome);
  const midias = Array.isArray(h.midias) ? h.midias : [];
  const paraGrupo = h.publico === PUBLICO_GRUPO && lista.length === 0;

  return (
    <div className="modal-overlay" onMouseDown={onClose}>
      <div className="modal-card" onMouseDown={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <div>
            <h3>{h.titulo}</h3>
            <p className="modal-sub">
              {h.publico || "Todos"}
              {paraGrupo ? "" : ` · ${h.enviados} pessoa(s)`} ·{" "}
              {new Date(h.criado_em).toLocaleString("pt-BR")}
              {h.criado_por ? ` · por ${h.criado_por}` : ""}
            </p>
          </div>
          <button type="button" className="icon-btn" onClick={onClose}>
            <IconClose size={18} />
          </button>
        </div>
        <div className="modal-body">
          {midias.length > 0 && (
            <div className="endo-midias-detalhe">
              {midias.map((m) => {
                const url = api.endomarketing.arte.url(m.arquivo);
                if (m.tipo === "imagem") {
                  return <img key={m.arquivo} src={url} alt={m.nome || `Imagem de ${h.titulo}`} />;
                }
                if (m.tipo === "video") {
                  return (
                    <video
                      key={m.arquivo}
                      src={`${url}#t=0.1`}
                      controls
                      playsInline
                      preload="metadata"
                      aria-label={m.nome}
                    />
                  );
                }
                return (
                  <a
                    key={m.arquivo}
                    className="endo-midia-pdf-link"
                    href={url}
                    target="_blank"
                    rel="noopener"
                    title={`Abrir ${m.nome}`}
                  >
                    <IconFile size={22} />
                    <span>{m.nome}</span>
                    {m.tamanho ? <small>{tamanhoLegivel(m.tamanho)}</small> : null}
                  </a>
                );
              })}
            </div>
          )}
          <div className="endo-balao">{h.mensagem}</div>
          <h4 className="endo-titulo">Quem recebeu</h4>
          <ul className="endo-dest-simples">
            {lista.map((d) => (
              <li key={d.id}>
                <span>{d.nome}</span>
                <small className="muted">{d.telefone || "—"}</small>
              </li>
            ))}
            {lista.length === 0 && (
              <li className="muted">
                {paraGrupo
                  ? "Enviado para um grupo do WhatsApp (a conversa foi escolhida na hora)."
                  : "Sem lista registrada."}
              </li>
            )}
          </ul>
        </div>
        <div className="modal-foot">
          <button type="button" className="btn-primary" onClick={onClose}>
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
}
